import React, { useState, useEffect } from 'react';
import { FaPhone, FaWhatsapp, FaEnvelope, FaInstagram, FaFacebook, FaArrowLeft } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { companyInfo, faqs } from '../data';

// Static Data
const socialLinks = [
  { name: 'Phone', icon: FaPhone, action: 'tel:+919207207469' },
  { name: 'WhatsApp', icon: FaWhatsapp, action: 'https://wa.me/919207207469' },
  { name: 'Email', icon: FaEnvelope, action: 'mailto:official.laynered@gmail.com' },
  { name: 'Instagram', icon: FaInstagram, action: 'https://www.instagram.com/layne_red?igsh=MW9hazVybmU4YmprMQ==' }, // Note: Instagram link points to Facebook; update if needed
  { name: 'Facebook', icon: FaFacebook, action: 'https://www.facebook.com/share/19BYY4Qj7K/?mibextid=wwXlfr' },
];

const footerLinks = [
  { name: 'Home', path: '/' },
  { name: 'Shop', path: '/shop' },
  { name: 'Support', path: '/support' },
  { name: 'Cart', path: '/cart' },
  { name: 'Orders', path: '/orders' },
];

const legalLinks = [
  { name: 'Privacy Policy', path: '/privacy-policy' },
  { name: 'Terms of Service', path: '/terms-of-service' },
  { name: 'Return Policy', path: '/return-policy' },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const faqViewVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: -20, transition: { duration: 0.3 } },
};

const FooterSection = () => {
  const [selectedFaq, setSelectedFaq] = useState(null);
  const location = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  return (
    <motion.footer
      className="bg-primary dark:bg-primary-dark text-black dark:text-white py-10 px-4 sm:px-6 lg:px-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <AnimatePresence mode="wait">
        {selectedFaq ? (
          <motion.div
            key="faq-view"
            className="max-w-7xl mx-auto flex flex-col items-start space-y-4"
            variants={faqViewVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <button
              onClick={() => setSelectedFaq(null)}
              className="flex items-center gap-2 text-[#DBAE6E] hover:text-[#c99d5d] transition-colors"
              aria-label="Back to footer"
            >
              <FaArrowLeft size={18} />
              <span className="text-sm font-semibold">Back</span>
            </button>
            <h3 className="text-xl font-semibold text-[#DBAE6E]">{selectedFaq.question}</h3>
            <p className="text-black dark:text-white text-base">{selectedFaq.answer}</p>
            <Link
              to="/support"
              className="text-sm text-[#DBAE6E] hover:text-[#c99d5d] font-semibold transition-colors"
            >
              Contact Support for More Questions
            </Link>
          </motion.div>
        ) : (
          <motion.div
            key="footer-view"
            className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={containerVariants}
          >
            {/* Company Information */}
            <motion.div variants={itemVariants} className="flex flex-col gap-3">
              <h3 className="text-xl font-semibold text-[#DBAE6E]">{companyInfo.name}</h3>
              <p className="text-sm text-black dark:text-white">{companyInfo.description}</p>
              <p className="text-sm text-black dark:text-white">
                <span className="font-semibold">Address:</span> {companyInfo.address}
              </p>
              <p className="text-sm text-black dark:text-white">
                <span className="font-semibold">Phone:</span>{' '}
                <a href={`tel:${companyInfo.phone}`} className="hover:text-[#DBAE6E] text-[#DBAE6E] transition-colors">
                  {companyInfo.phone}
                </a>
              </p>
              <p className="text-sm text-black dark:text-white">
                <span className="font-semibold">Email:</span>{' '}
                <a href={`mailto:${companyInfo.email}`} className="hover:text-[#DBAE6E] text-[#DBAE6E] transition-colors">
                  {companyInfo.email}
                </a>
              </p>
            </motion.div>

            {/* Navigation Links */}
            <motion.div variants={itemVariants} className="flex flex-col gap-3">
              <h3 className="text-lg font-semibold text-[#DBAE6E]">Quick Links</h3>
              <ul className="space-y-1">
                {footerLinks.map(({ name, path }) => (
                  <li key={name}>
                    <Link
                      to={path}
                      className="text-sm text-black dark:text-white hover:text-[#DBAE6E] transition-colors"
                    >
                      {name}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* FAQ Section */}
            <motion.div variants={itemVariants} className="flex flex-col gap-3">
              <h3 className="text-lg font-semibold text-[#DBAE6E]">FAQs</h3>
              <ul className="space-y-1">
                {faqs.map((faq, index) => (
                  <li key={index}>
                    <button
                      onClick={() => setSelectedFaq(faq)}
                      className="text-sm text-black dark:text-white hover:text-[#DBAE6E] transition-colors text-left w-full"
                    >
                      {faq.question}
                    </button>
                  </li>
                ))}
                <li>
                  <Link
                    to="/support"
                    className="text-sm text-[#DBAE6E] hover:text-[#c99d5d] font-semibold transition-colors"
                  >
                    More Questions?
                  </Link>
                </li>
              </ul>
            </motion.div>

            {/* Contact Section */}
            <motion.div variants={itemVariants} className="flex flex-col gap-3">
              <h3 className="text-lg font-semibold text-[#DBAE6E]">Social</h3>
              <div className="flex gap-4">
                {socialLinks.map(({ name, icon: Icon, action }) => (
                  <a
                    key={name}
                    href={action}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={name}
                    className="text-[#DBAE6E] hover:text-[#c99d5d] transition-colors"
                  >
                    <Icon size={22} />
                  </a>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Legal Links and Copyright */}
      <motion.div
        variants={itemVariants}
        className="max-w-7xl mx-auto mt-8 pt-6 border-t border-gray-800 flex flex-col sm:flex-row justify-between items-center gap-4 text-center sm:text-left"
      >
        <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
          {legalLinks.map(({ name, path }) => (
            <Link
              key={name}
              to={path}
              className="text-sm text-black dark:text-white hover:text-[#DBAE6E] transition-colors"
            >
              {name}
            </Link>
          ))}
        </div>
        <p className="text-sm text-black dark:text-white">
          © {new Date().getFullYear()} {companyInfo.name}. All rights reserved.
        </p>
      </motion.div>
    </motion.footer>
  );
};

export default FooterSection;